<template>
    <div>
        <b-modal
            :visible="visible"
            id="admin-role-modal"
            size="lg"
            :ok-title="`${role ? 'Edit' : 'Add'} Role`"
            :ok-disabled="!validated && !saving"
            @cancel="cancel"
            @close="cancel"
            @ok.prevent="save"
            @hidden="reset"
            no-close-on-esc
        >
            <template #modal-title>
                <span v-if="role">Edit</span>
                <span v-else>Add</span>
                Role
            </template>

            <b-form-group label="Name" label-for="role-modal-name">
                <b-form-input
                    id="role-modal-name"
                    type="text"
                    placeholder="Enter Name"
                    aria-label="name"
                    v-model="role_name"
                    :state="validated"
                    required
                />
                <b-form-invalid-feedback :state="validated" v-if="!saving">
                    {{ invalidFeedback }}
                </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group label-for="role-modal-permissions">
                <template #label>
                    Permissions
                    <b-button id="role-modal-permissions-tip" variant="link" class="p-0">
                        <font-awesome-icon :icon="['fad', 'info-circle']" />
                    </b-button>
                    <b-tooltip target="role-modal-permissions-tip" triggers="hover">
                        (Optional) Permissions to assign to the role, upon creation/modification.
                    </b-tooltip>
                </template>

                <multiselect
                    id="role-modal-permissions"
                    v-model="selected_permissions"
                    placeholder="Select Permissions"
                    label="name"
                    track-by="id"
                    :options="permissions"
                    :multiple="true"
                />
            </b-form-group>
        </b-modal>
    </div>
</template>

<style></style>

<script>
import { mapState } from 'vuex'
import moment from 'moment'

export default {
    data: () => ({
        role_name: '',
        saving: false,
        selected_permissions: null,
    }),
    props: {
        visible: Boolean,
        role: Object,
    },
    components: {

    },
    computed: {
        ...mapState({
            errors: state => state.errors.errors,
            user: state => state.user.user,
            roles: state => state.admin.roles,
            permissions: state => state.admin.permissions,
        }),
        invalidFeedback() {
            if (!this.nameIsSet) {
                return 'Name is required.'
            }
            if (this.nameIsDuplicated) {
                return 'This name already exists.'
            }
        },
        validated() {
            return this.nameIsSet && this.nameIsDuplicated === false
        },
        nameIsSet() {
            return this.role_name.length !== 0
        },
        nameIsDuplicated() {
            const nameAlreadyExists = this.roleNames.includes(this.role_name)
            if (this.role) {
                return this.role.name !== this.role_name && nameAlreadyExists
            }

            return nameAlreadyExists
        },
        roleNames() {
            return this.roles.map(role => role.name)
        }
    },
    methods: {
        cancel() {
            this.$emit('hide')
        },
        async save() {
            try {
                this.saving = true
                const roleData = {
                    name: this.role_name,
                    permissions: this.selected_permissions,
                }

                if (!this.role) {
                    await this.$store.dispatch('createAdminRole', roleData)
                } else {
                    await this.$store.dispatch('updateAdminRole', {
                        ...roleData,
                        id: this.role.id,
                    })
                }

                // reload the stores that would be updated
                await this.$store.dispatch('getAdminRoles')
                await this.$store.dispatch('getAdminPermissions')

                this.$emit('hide')
            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Failed to save role: ${e.message}`,
                    },
                    time: moment().toISOString(),
                })
            }
        },
        reset() {
            this.saving = false

            if (!this.role) {
                this.role_name = ''
                this.selected_permissions = null
            } else {
                this.role_name = this.role.name
                this.selected_permissions = this.role.permissions
            }
        },
    },
    mounted() {
        this.$root.$on('bv::modal::show', (bvEvent, modalId) => {
            if (modalId !== 'admin-role-modal') {
                return
            }

            this.reset()
        })
    }
}
</script>
